import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { motion } from 'framer-motion';
import { Lock, Shield } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/Card';

const items = [
  { title: 'Data encryption', description: 'Data in transit is protected with TLS. Sensitive data at rest is encrypted using industry-standard practices.', icon: Lock, iconBg: 'bg-primary/15 text-primary' },
  { title: 'Access control', description: 'Access to systems and data is restricted by role and need. We use strong authentication and least-privilege principles.', icon: Shield, iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  { title: 'Monitoring & response', description: 'We monitor for anomalies and security events and have processes to respond to incidents and notify affected users where required.', icon: Lock, iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
];

export const SecurityPage: React.FC = () => {
  useSEO({ title: 'Security - How We Protect Your Data', description: 'Five AI security practices: end-to-end encryption, access control, monitoring, GDPR compliance, and zero data sharing. Your career data is safe with us.', path: '/security' });

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Security">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-4xl mx-auto">
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Trust & safety</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Security</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">first</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">How we protect your data and our systems.</p>
              <p className="text-sm text-muted-foreground/80">Encryption, access control, monitoring</p>
            </motion.div>
          </div>
        </section>
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid sm:grid-cols-3 gap-6">
              {items.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                    <Card hover className="h-full p-6 sm:p-8 rounded-2xl border-2 hover:border-primary/40 transition-all duration-300">
                      <div className={`w-11 h-11 rounded-xl ${item.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3">{item.title}</h2>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
